import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, computed, effect, inject, Signal, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  AriaEnhancedDirective,
  ConfigService,
  DEFAULT_CONFIG,
  GsIconComponent,
  GsResponsiveDirective,
  FooterBlock,
  ICompanyInfo,
  IConfigFooterBottombar,
  IConfigFooterInfoarea,
  IConfigFooterMenuarea,
  IInitMenu,
  ImageAccessibilityDirective,
  ImageService,
  InformationService,
  IPaymentMethod,
  IShippingMethod,
  OpeningTimesService,
  SOCIAL_MEDIA_ICON_MAP,
  StyleService,
  SystemService
} from 'libs/shared/src';
import { FooterService } from '../services/footer.service';

@Component({
  selector: 'footer-basic',
  imports: [
    CommonModule,
    NgOptimizedImage,
    TranslatePipe,
    GsIconComponent,
    AriaEnhancedDirective /* Meier-Template */,
    ImageAccessibilityDirective,
    GsResponsiveDirective
  ],
  templateUrl: './footer-basic.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: block;
    }
  `
})
export class FooterBasicComponent {
  // Services
  protected readonly system = inject(SystemService);
  protected readonly imageService = inject(ImageService);
  protected readonly informationService = inject(InformationService);
  protected readonly styleService = inject(StyleService);
  protected readonly config = inject(ConfigService);
  protected readonly footerService = inject(FooterService);
  private readonly openingTimesService = inject(OpeningTimesService);

  // References
  DEFAULT_CONFIG = DEFAULT_CONFIG;
  SOCIAL_MEDIA_ICON_MAP = SOCIAL_MEDIA_ICON_MAP;

  // Properties & Signals
  /** Available payment methods */
  paymentMethods: Signal<IPaymentMethod[] | undefined> = computed(() => {
    return this.informationService.init()?.paymentMethods;
  });
  /** Available payment methods */
  shippingMethods: Signal<IShippingMethod[] | undefined> = computed(() => {
    return this.informationService.init()?.shippingMethods;
  });
  /** Regular opening times with a client-rendered localized range string (store/user TZ aware). */
  public openingHours = computed(() => {
    return (this.informationService.init()?.companyInfo?.openingTimesInfos ?? []).map((item) => ({
      ...item,
      title: this.openingTimesService.formatDayLabel(item.title),
      formattedRange: this.openingTimesService.formatEntryRangeLocalized(item)
    }));
  });
  /**
   * Upcoming special opening times grouped by `openingTimeName`. Each group collapses backend
   * entries describing the same period (e.g. Mo–Fr + Sat) into a single visible block with
   * one shared label and date range; the per-day entries are exposed as sub-entries.
   */
  public specialOpeningTimeGroups = computed(() => {
    return this.openingTimesService.getUpcomingSpecialOpeningTimeGroups(
      this.informationService.init()?.companyInfo?.specialOpeningTimesInfo
    );
  });
  /** Footer menu items */
  footerMenu: Signal<IInitMenu[]> = computed(() => {
    const menus = this.informationService.init()?.menus;
    if (!menus) return [];

    const footerMenu = menus['cs4-footer']?.length > 0 ? menus['cs4-footer'] : [];
    const mobileFooterMenu = menus['cs4-mobileFooter'];
    if (this.styleService.isMobile() && mobileFooterMenu?.length > 0) {
      return mobileFooterMenu;
    }
    return footerMenu;
  });
  /** Info area configuration for the footer */
  configInfoarea: Signal<IConfigFooterInfoarea | undefined> = computed(() => {
    return this.config.config()?.footer?.infoarea;
  });
  /** Main area configuration for the footer */
  configMenuarea: Signal<IConfigFooterMenuarea | undefined> = computed(() => {
    return this.config.config()?.footer?.menuarea;
  });
  /** Bottom bar configuration for the footer */
  configBottombar: Signal<IConfigFooterBottombar | undefined> = computed(() => {
    return this.config.config()?.footer?.bottombar;
  });
  /** Social media links */
  socialMediaLinks: Signal<Record<string, string> | undefined> = computed(() => {
    return this.informationService.init()?.socialMediaLinks ?? {};
  });
  /** App links */
  appLinks: Signal<Record<string, string> | undefined> = computed(() => {
    return this.informationService.init()?.appLinks ?? {};
  });
  /** Company information if available, otherwise undefined. */
  companyInfo: Signal<ICompanyInfo | undefined> = computed(() => {
    return this.informationService.init()?.companyInfo;
  });
  /** Whether to use container class for layout */
  useContainer = computed(() => {
    const layout = this.config.config()?.footer?.layout;
    return layout === 'LayoutContent';
  });
  /** Logo link URL (undefined if not set) */
  logoLink: Signal<string> = computed<string>(() => {
    const infoarea = this.configInfoarea();
    if (infoarea?.logo?.useHomeLinkTarget) return '/';
    if (infoarea?.logo?.linkUrl && infoarea.logo.linkUrl.length > 0) return infoarea.logo.linkUrl;
    return '/';
  });
  /** Visible blocks below the footer menu, sorted by their configured order index */
  footerBlocks: Signal<FooterBlock[]> = computed(() => {
    const infoarea = this.configInfoarea();
    const defaultInfoarea = DEFAULT_CONFIG.footer?.infoarea;
    const order = this.config.config()?.footer?.blockOrder;
    const defaultOrder = DEFAULT_CONFIG.footer?.blockOrder;
    const blocks: Record<FooterBlock, boolean> = {
      paymentMethods:
        !!(infoarea?.paymentMethods?.active ?? defaultInfoarea?.paymentMethods?.active) && this.footerService.hasPaymentMethods(),
      deliveryMethods:
        !!(infoarea?.deliveryMethods?.active ?? defaultInfoarea?.deliveryMethods?.active) && this.footerService.hasShippingMethods(),
      socialMediaLinks:
        !!(infoarea?.socialMediaLinks?.active ?? defaultInfoarea?.socialMediaLinks?.active) && this.footerService.hasSocialMediaLinks(),
      appLinks: !!(infoarea?.appLinks?.active ?? defaultInfoarea?.appLinks?.active) && this.footerService.hasAppLinks(),
      revocation: this.footerService.showRevocation()
    };
    return (Object.keys(blocks) as FooterBlock[])
      .filter((block) => blocks[block])
      .sort((a, b) => (order?.[a] ?? defaultOrder?.[a] ?? 0) - (order?.[b] ?? defaultOrder?.[b] ?? 0));
  });
  /**
   * Number of block columns next to the infoarea column. Follows the menuarea columns so the blocks
   * line up with the menus, but widens when more blocks are active than the menuarea has columns —
   * otherwise the extra blocks would wrap into a second row.
   */
  footerBlockColumns = computed(() => {
    const cols = this.configMenuarea()?.columns ?? DEFAULT_CONFIG.footer?.menuarea?.columns ?? 3;
    return Math.max(cols, this.footerBlocks().length - 1);
  });
  /** CSS value for grid-template-columns based on configured column count */
  menuareaGridColumns = computed(() => {
    const cols = this.configMenuarea()?.columns ?? DEFAULT_CONFIG.footer?.menuarea?.columns ?? 3;
    return `repeat(${cols}, minmax(0, 1fr))`;
  });

  constructor() {
    effect(() => {
      this.openingTimesService.setStoreTimeZone(this.companyInfo()?.timeZone);
    });
  }
}
